import { useTranslation } from 'react-i18next';
import {
  FormField,
  NumberInput,
  TextInput,
  Textarea,
  formatAddress,
} from '@vipso/shared';
import type { Customer } from '../../api/tenantApi';

export interface CustomerFormValues {
  name: string;
  phone: string;
  email: string;
  address: string;
  openingBalance: number;
}

export const EMPTY_CUSTOMER_FORM: CustomerFormValues = {
  name: '',
  phone: '',
  email: '',
  address: '',
  openingBalance: 0,
};

export function customerFormValues(customer: Customer): CustomerFormValues {
  return {
    name: customer.name,
    phone: customer.phone || '',
    email: customer.email || '',
    address: formatAddress(customer.address),
    openingBalance: customer.openingBalance ?? 0,
  };
}

/**
 * Payload for `addUpdateCustomer`. The free-text address collapses into
 * `line1`, reusing the existing address row id when one is present.
 */
export function customerFormInput(
  values: CustomerFormValues,
  existing?: Customer | null,
): Record<string, unknown> {
  const addressText = values.address.trim();
  return {
    ...(existing ? { id: existing.id } : {}),
    name: values.name,
    phone: values.phone || null,
    email: values.email || null,
    address: addressText
      ? {
          ...(existing?.address?.id ? { id: existing.address.id } : {}),
          line1: addressText,
        }
      : null,
    openingBalance: values.openingBalance,
  };
}

export function CustomerForm({
  value,
  onChange,
}: {
  value: CustomerFormValues;
  onChange: (next: CustomerFormValues) => void;
}) {
  const { t } = useTranslation();
  const set = <K extends keyof CustomerFormValues>(
    key: K,
    next: CustomerFormValues[K],
  ) => onChange({ ...value, [key]: next });

  return (
    <>
      <FormField label={t('common.name')} required>
        <TextInput
          value={value.name}
          onChange={(e) => set('name', e.currentTarget.value)}
        />
      </FormField>
      <FormField label={t('common.phone')}>
        <TextInput
          value={value.phone}
          onChange={(e) => set('phone', e.currentTarget.value)}
        />
      </FormField>
      <FormField label={t('common.email')}>
        <TextInput
          value={value.email}
          onChange={(e) => set('email', e.currentTarget.value)}
        />
      </FormField>
      <FormField label={t('common.address')}>
        <Textarea
          value={value.address}
          onChange={(e) => set('address', e.currentTarget.value)}
        />
      </FormField>
      <FormField label={t('customers.openingBalance')}>
        <NumberInput
          value={value.openingBalance}
          onChange={(next) => set('openingBalance', Number(next) || 0)}
        />
      </FormField>
    </>
  );
}
